import { createTheme, adaptV4Theme } from '@mui/material/styles';

const baseTypography = {
  fontFamily:
    '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  h1: { fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { fontWeight: 700, letterSpacing: '-0.02em' },
  h3: { fontWeight: 700, letterSpacing: '-0.015em' },
  h4: { fontWeight: 700, letterSpacing: '-0.01em' },
  h5: { fontWeight: 600, letterSpacing: '-0.005em' },
  h6: { fontWeight: 600 },
  button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  body1: { lineHeight: 1.55 },
  body2: { lineHeight: 1.5 },
};

const baseShape = { borderRadius: 14 };

const buildComponents = (mode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        paddingInline: 18,
        paddingBlock: 9,
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
      },
      containedPrimary: {
        background:
          mode === 'dark'
            ? 'linear-gradient(180deg, #ffffff 0%, #d4d4d8 100%)'
            : 'linear-gradient(180deg, #2a2d36 0%, #15171c 100%)',
        color: mode === 'dark' ? '#0d0e12' : '#f5f5f7',
        '&:hover': {
          background:
            mode === 'dark'
              ? 'linear-gradient(180deg, #f5f5f7 0%, #c4c4c8 100%)'
              : 'linear-gradient(180deg, #34373f 0%, #1d1f25 100%)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 18,
        border:
          mode === 'dark'
            ? '1px solid rgba(255,255,255,0.06)'
            : '1px solid rgba(15,23,42,0.06)',
        backgroundImage: 'none',
        boxShadow:
          mode === 'dark'
            ? '0 1px 2px rgba(0,0,0,0.4)'
            : '0 1px 2px rgba(15,23,42,0.04)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 20 },
    },
  },
  MuiTextField: {
    defaultProps: { variant: 'outlined', size: 'small' },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: 12 },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 8, fontWeight: 500 },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 8,
        fontSize: 12,
        backgroundColor: mode === 'dark' ? '#1e1f25' : '#0f172a',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
    },
  },
});

const lightPalette = {
  mode: 'light',
  primary: { main: '#1a1c22', light: '#3a3e49', dark: '#0d0e12', contrastText: '#f5f5f7' },
  secondary: { main: '#6b7280', light: '#9ca3af', dark: '#4b5563', contrastText: '#fff' },
  success: { main: '#16a34a' },
  warning: { main: '#d97706' },
  error: { main: '#dc2626' },
  info: { main: '#475569' },
  background: {
    default: '#f7f8fa',
    paper: '#ffffff',
  },
  text: {
    primary: '#15171c',
    secondary: '#6b7280',
  },
  divider: 'rgba(15,17,28,0.08)',
};

const darkPalette = {
  mode: 'dark',
  primary: { main: '#f5f5f7', light: '#ffffff', dark: '#d4d4d8', contrastText: '#0d0e12' },
  secondary: { main: '#9ca3af', light: '#d1d5db', dark: '#6b7280', contrastText: '#0d0e12' },
  success: { main: '#22c55e' },
  warning: { main: '#fbbf24' },
  error: { main: '#f87171' },
  info: { main: '#94a3b8' },
  background: {
    default: '#0d0e12',
    paper: '#16181f',
  },
  text: {
    primary: '#f5f5f7',
    secondary: '#9ca3af',
  },
  divider: 'rgba(255,255,255,0.08)',
};

export const buildTheme = (mode = 'light') => createTheme(adaptV4Theme({
  palette: mode === 'dark' ? darkPalette : lightPalette,
  typography: baseTypography,
  shape: baseShape,
  components: buildComponents(mode),
}));
